import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Phone, MessageCircle, ArrowRight, Save } from "lucide-react";
import { api, fmt, errMsg, waLink, telLink } from "@/lib/api";

const STATUS_CLS = {
  "حجز مؤكد": "text-green-300 bg-green-500/10",
  "مهتم ولم يحجز": "text-gold bg-gold/10",
  "زبون سابق": "text-blue-300 bg-blue-500/10",
  "حجز ملغى": "text-red-300 bg-red-500/10",
};

export default function Customers() {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const { data } = useQuery({
    queryKey: ["customers", q],
    queryFn: async () => (await api.get("/admin/customers", { params: { q: q || undefined } })).data,
    refetchInterval: 6000,
  });

  if (selected) return <CustomerDetail phone={selected} onBack={() => setSelected(null)} />;

  return (
    <div className="space-y-4" data-testid="customers-page">
      <h1 className="font-display text-2xl">الزبائن</h1>
      <div className="relative">
        <Search size={18} className="absolute top-1/2 -translate-y-1/2 start-3 text-muted2" />
        <input data-testid="customers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو رقم الهاتف"
          className="w-full card-lux p-3 ps-10 bg-navy2 text-offwhite outline-none focus:ring-1 focus:ring-gold" />
      </div>
      <div className="space-y-2">
        {(data || []).map((c) => (
          <button key={c.phone} data-testid={`customer-${c.phone}`} onClick={() => setSelected(c.phone)}
            className="card-lux p-4 w-full flex items-center justify-between text-start hover:bg-navy3/40 transition-colors">
            <div>
              <p className="text-offwhite font-medium">{c.name}</p>
              <p className="text-xs text-muted2">{c.phone} · {c.event_type} · {c.event_date}</p>
            </div>
            <span className={`text-[11px] px-2.5 py-1 rounded-full ${STATUS_CLS[c.status] || "text-muted2 bg-navy3"}`}>{c.status}</span>
          </button>
        ))}
        {!data?.length && <p className="text-muted2/60 text-sm card-lux p-4">لا يوجد زبائن.</p>}
      </div>
    </div>
  );
}

function CustomerDetail({ phone, onBack }) {
  const navigate = useNavigate();
  const { data, refetch } = useQuery({ queryKey: ["customer", phone], queryFn: async () => (await api.get(`/admin/customers/${phone}`)).data });
  const [notes, setNotes] = useState(null);

  if (!data) return <p className="text-muted2">جارٍ التحميل...</p>;
  const c = data.customer;
  const noteVal = notes === null ? (c.internal_notes || "") : notes;

  const saveNotes = async () => {
    try { await api.patch(`/admin/customers/${phone}/notes`, { internal_notes: noteVal }); toast.success("تم حفظ الملاحظات"); refetch(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="customer-detail">
      <button onClick={onBack} className="text-gold flex items-center gap-1 text-sm"><ArrowRight size={18} /> رجوع</button>
      <div className="card-lux p-5">
        <h1 className="font-display text-2xl mb-1">{c.name}</h1>
        <span className={`text-[11px] px-2.5 py-1 rounded-full ${STATUS_CLS[c.status] || "text-muted2 bg-navy3"}`}>{c.status}</span>
        <div className="flex gap-2 my-4">
          <a href={telLink(c.phone)} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full glass text-sm"><Phone size={16} className="text-gold" /> اتصال</a>
          <a href={waLink(c.phone)} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full glass text-sm"><MessageCircle size={16} className="text-gold" /> واتساب</a>
        </div>
        <div className="space-y-2 text-sm">
          <Row k="الهاتف" v={c.phone} />
          <Row k="أول تواصل" v={(c.first_contact || "").slice(0, 10)} />
          <Row k="المكان" v={c.venue} />
          <Row k="نوع المناسبة" v={c.event_type} />
        </div>
      </div>

      <div className="card-lux p-5">
        <h2 className="text-sm text-gold mb-2">ملاحظاتي (خاصة)</h2>
        <textarea data-testid="customer-notes" value={noteVal} onChange={(e) => setNotes(e.target.value)} rows={3}
          className="w-full card-lux p-3 bg-navy2 text-offwhite outline-none focus:ring-1 focus:ring-gold resize-none mb-2" />
        <button onClick={saveNotes} className="btn-gold px-5 py-2 rounded-full text-sm flex items-center gap-1"><Save size={14} /> حفظ</button>
      </div>

      <div>
        <h2 className="text-sm text-muted2 mb-2">حجوزات الزبون</h2>
        <div className="space-y-2">
          {data.bookings.map((b) => (
            <button key={b.id} onClick={() => navigate(`/admin/booking/${b.id}`)} className="card-lux p-3 w-full flex justify-between text-start hover:bg-navy3/40 transition-colors">
              <span className="text-offwhite text-sm">{b.date} · {b.venue_name}</span>
              <span className="gold-text text-xs">{b.booking_number || b.status}</span>
            </button>
          ))}
          {!data.bookings.length && <p className="text-muted2/60 text-sm">لا حجوزات.</p>}
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (<div className="flex justify-between"><span className="text-muted2">{k}</span><span className="text-offwhite">{v}</span></div>);
